import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { getCorrectCounts, getWrongCounts } from "./wrongCountStorage";
import "./MistakeStats.css";

const slideVariants = {
    initial: { x: "100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "-100%", opacity: 0 },
};

function MistakeStats() {
    const [words, setWords] = useState([]);
    const [error, setError] = useState("");
    const [dictId, setDictId] = useState(0);
    const [rangeStart, setRangeStart] = useState("");
    const [rangeEnd, setRangeEnd] = useState("");

    useEffect(() => {
        const jsonFile = dictId === 0 ? "eitango.json" : "sokutan.json";
        fetch(`${process.env.PUBLIC_URL}/${jsonFile}`)
            .then((res) => res.json())
            .then((data) => {
                setWords(Array.isArray(data) ? data : []);
                setError("");
            })
            .catch(() => setError("データの読み込みに失敗しました。"));
    }, [dictId]);

    const presetOptions = useMemo(() => {
        const list = [{ label: "すべての範囲", start: "", end: "" }];
        if (words.length === 0) return list;
        const blockCount = Math.ceil(words.length / 100);
        for (let i = 0; i < blockCount; i++) {
            const start = i * 100 + 1;
            const end = Math.min((i + 1) * 100, words.length);
            list.push({
                label: `${start} ~ ${end}`,
                start: String(start),
                end: String(end)
            });
        }
        return list;
    }, [words]);

    const currentPresetIndex = useMemo(() => {
        const index = presetOptions.findIndex(
            (opt) => opt.start === String(rangeStart) && opt.end === String(rangeEnd)
        );
        return index !== -1 ? index : -1;
    }, [presetOptions, rangeStart, rangeEnd]);

    const rows = useMemo(() => {
        const wrongCounts = getWrongCounts(dictId);
        const correctCounts = getCorrectCounts(dictId);
        const mapped = words.map((item, index) => {
            const english = item[0];
            const japanese = item[1];
            const wrongCount = wrongCounts[english] || 0;
            const correctCount = correctCounts[english] || 0;
            const totalCount = wrongCount + correctCount;
            const accuracy = totalCount === 0 ? 0 : (correctCount / totalCount) * 100;

            return {
                no: index + 1,
                english,
                japanese,
                wrongCount,
                correctCount,
                accuracy,
            };
        });

        const startNo = rangeStart !== "" ? parseInt(rangeStart, 10) : 1;
        const endNo = rangeEnd !== "" ? parseInt(rangeEnd, 10) : words.length;

        const filtered = mapped.filter((row) => {
            const meetsStart = isNaN(startNo) || row.no >= startNo;
            const meetsEnd = isNaN(endNo) || row.no <= endNo;
            return meetsStart && meetsEnd;
        });

        return filtered.sort((a, b) => {
            const aHasAnswered = a.correctCount + a.wrongCount > 0;
            const bHasAnswered = b.correctCount + b.wrongCount > 0;

            if (aHasAnswered !== bHasAnswered) {
                return aHasAnswered ? -1 : 1;
            }
            if (a.accuracy !== b.accuracy) {
                return a.accuracy - b.accuracy;
            }
            if (b.wrongCount !== a.wrongCount) {
                return b.wrongCount - a.wrongCount;
            }
            return a.no - b.no;
        });
    }, [words, dictId, rangeStart, rangeEnd]);

    return (
        <motion.div
            variants={slideVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="MistakeStats"
        >
            <Link
                to="/mode"
                className="back"
            >
                &lt; もどる
            </Link>
            <h1 className="title">ミス統計を見る</h1>

            <div className="filterPanel" id="statsFilterPanel">
                <div className="filterTabs" id="statsDictTabs">
                    <button
                        type="button"
                        id="statsTabBtnEitango"
                        className={`filterTab ${dictId === 0 ? "active" : ""}`}
                        onClick={() => {
                            setDictId(0);
                            setRangeStart("");
                            setRangeEnd("");
                        }}
                    >
                        東進 英単語1800
                    </button>
                    <button
                        type="button"
                        id="statsTabBtnSokutan"
                        className={`filterTab ${dictId === 1 ? "active" : ""}`}
                        onClick={() => {
                            setDictId(1);
                            setRangeStart("");
                            setRangeEnd("");
                        }}
                    >
                        速読英単語1903
                    </button>
                </div>

                <div className="rangeFilter" id="statsRangeFilter">
                    <div className="rangeInputGroup" id="statsPresetGroup">
                        <span className="filterLabel">簡易選択:</span>
                        <select
                            id="statsPresetSelect"
                            value={currentPresetIndex !== -1 ? currentPresetIndex : "custom"}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "custom") {
                                    // Let user edit start/end manually
                                } else {
                                    const opt = presetOptions[parseInt(val, 10)];
                                    setRangeStart(opt.start);
                                    setRangeEnd(opt.end);
                                }
                            }}
                            className="presetSelect"
                        >
                            {presetOptions.map((opt, idx) => (
                                <option key={idx} value={idx}>
                                    {opt.label}
                                </option>
                            ))}
                            <option value="custom">カスタム範囲</option>
                        </select>
                    </div>

                    <div className="rangeInputGroup" id="statsInputsGroup">
                        <span className="filterLabel">範囲指定:</span>
                        <input
                            type="number"
                            id="statsRangeStartInput"
                            min="1"
                            max={words.length || undefined}
                            placeholder="開始"
                            value={rangeStart}
                            onChange={(e) => setRangeStart(e.target.value)}
                            className="rangeInput"
                        />
                        <span className="rangeSeparator">~</span>
                        <input
                            type="number"
                            id="statsRangeEndInput"
                            min="1"
                            max={words.length || undefined}
                            placeholder="終了"
                            value={rangeEnd}
                            onChange={(e) => setRangeEnd(e.target.value)}
                            className="rangeInput"
                        />
                    </div>
                </div>

                <div className="filterSummary" id="statsFilterSummary">
                    表示中: <strong className="highlight">{rows.length}</strong>語
                    {words.length > 0 && ` (全 ${words.length}語 中 ${((rows.length / words.length) * 100).toFixed(1)}%)`}
                </div>
            </div>

            {error ? (
                <p>{error}</p>
            ) : (
                <div className="statsTableWrap">
                    <table className="statsTable">
                        <thead>
                            <tr>
                                <th>English</th>
                                <th>日本語</th>
                                <th style={{width: "7%" }}>正解</th>
                                <th style={{width: "7%" }}>ミス</th>
                                <th>正解率</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.no}>
                                    <td>{row.english}</td>
                                    <td>{row.japanese}</td>
                                    <td>{row.correctCount}</td>
                                    <td>{row.wrongCount}</td>
                                    <td>{row.accuracy.toFixed(1)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </motion.div>
    );
}

export default MistakeStats;
